import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaStar,
  FaChartLine,
  FaArrowRight,
} from "react-icons/fa";
import { MdMoreVert } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Loader from "../../Loader/Loader"; // adjust the path
import AwardDetail from "./AwardDetail";
import { hitGetBidListingProfessionalApi } from "../../../Redux/Professional/BidListingProfessionalSlice";
import { timeAgo, formatDate } from "../../../Helper/CommonFunctions";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";

const Bids = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("bid-placed");
  const [bidsData, setBidsData] = useState([]);
  const [AwardDetailShow, setAwardDetailShow] = useState(false);
  const [selectedBidData, setSelectedBidData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setGlobalLoading } = useGlobalState();

  // ✅ Set initial tab based on location state
  useEffect(() => {
    if (location.state && location.state.bidType) {
      if (location.state.bidType === "submitted") {
        setActiveTab("bid-placed");
      } else if (location.state.bidType === "draft") {
        setActiveTab("drafts");
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (!localStorage.getItem("professionalToken")) {
      navigate("/professional/login");
      return;
    }
    // ✅ Initial load is already handled below by activeTab effect
  }, []);

  // Redux state
  const responseBidsListProfessional = useSelector(
    (state) => state.getBidListProfessionalReducer.data
  );

  // ✅ Handle API calls based on activeTab
  useEffect(() => {
    if (!localStorage.getItem("professionalToken")) return;

    const typeMap = {
      "bid-placed": 1,
      awarded: 2,
      completed: 3,
      drafts: 5,
    };

    const type = typeMap[activeTab];
    if (type) {
      setLoading(true);
      setGlobalLoading(true);
      dispatch(hitGetBidListingProfessionalApi({ type })).then(() => {
        setLoading(false);
        setGlobalLoading(false);
      });
    }

    setAwardDetailShow(false);
  }, [activeTab, dispatch]);

  // ✅ Update local bids data
  useEffect(() => {
    if (responseBidsListProfessional?.status) {
      setLoading(false);
      setGlobalLoading(false);
      setBidsData(responseBidsListProfessional.data || []);
    }
  }, [responseBidsListProfessional]);

  if (loading) {
    setGlobalLoading(true);
    return <Loader />;
  }

  const tabs = [
    { id: "awarded", label: "Awarded", count: 1 },
    { id: "bid-placed", label: "Bid Placed", count: 12 },
    { id: "completed", label: "Completed", count: 0 },
    { id: "drafts", label: "Drafts", count: 0 },
  ];

  //const bidsData = bidsData.filter((bid) => bid.status === activeTab);

  const handleViewDetails = (bidData) => {
    //console.log("handle view details calling", bidData.jobId.jobId);
    setSelectedBidData(bidData);
    setAwardDetailShow(true);
  };

  if (loading) {
    return <div style={{ height: "100vh" }}><Loader /></div>;
  }

  return (
    <div className="bids-page pro-app-page">
      <div className="custom_container">
        <Row className="justify-content-center">
          <Col xl={8} lg={AwardDetailShow ? 12 : 10}>
            {/* Tabs */}
            <div className="bids-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`tab-btn ${activeTab === tab.id ? "active" : ""} ${tab.id === "awarded" ? "awarded-tab" : ""
                    }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="tab-count">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Bids List */}
            <div className="bids-list">
              {bidsData.length > 0 ? (
                bidsData.map((bid) => {
                  const scheduleStart =
                    bid.jobId?.schdeuledDateStart ||
                    bid.jobId?.scheduledDateStart;
                  const scheduleEnd =
                    bid.jobId?.scheduledDateEnd ||
                    bid.jobId?.scheduledDateEnd;
                  const formattedStart = formatDate(scheduleStart);
                  const formattedEnd = formatDate(scheduleEnd);

                  return (
                  <div key={bid._id} className="bid-card">
                    {/* Status Badge */}
                    <div
                      className={`status-badge d-flex align-items-center ${bid.status === "awarded" ? "awarded" : "bid-placed"
                        }`}
                    >
                      <div className="job-id"> Job {bid.jobId.jobId}</div>
                      <div className="job-date">
                        {" "}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="clock-icon"
                        >
                          <circle
                            cx="8"
                            cy="8"
                            r="7"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M8 4v4l2.5 2.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        Updated {timeAgo(bid.updatedAt)}{" "}
                      </div>
                    </div>

                    {/* Scheduled Dates */}
                    {formattedStart && (
                      <div className="bid-schedule">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="schedule-icon"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="17"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M3 9h18M8 2v4M16 2v4"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span>
                          Scheduled: {formattedStart}
                          {formattedEnd ? ` - ${formattedEnd}` : ""}
                        </span>
                      </div>
                    )}

                    {/* Bid Header */}
                    <div className="bid-header">
                      <div className="bid-header-left">
                        <div className="job-icon">
                          <img
                            src={
                              bid.userId.profileImage ||
                              "https://via.placeholder.com/50"
                            }
                            alt="Job"
                            className="job-image"
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "8px  ",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </div>
                        <div className="bid-title-section">
                          <h3 className="bid-title">{bid.jobId.title}</h3>
                          <div className="bid-location">
                            <FaMapMarkerAlt className="location-icon" />
                            <span>
                              {bid.jobId.city}, {bid.jobId.state}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="view-details-btn"
                        onClick={() =>
                          bid.status === 2 || bid.status === 3
                            ? handleViewDetails(bid)
                            : navigate(
                              `/professional/job-details/${bid.jobId._id}`
                            )
                        }
                      >
                        View Details
                      </button>
                    </div>

                    {/* Bid Description */}
                    <div className="bid-description d-flex align-items-center">
                      <img
                        src={
                          bid.jobId.media && bid.jobId.media.length > 0
                            ? bid.jobId.media[0]
                            : "https://via.placeholder.com/100"
                        }
                        alt="Job"
                        className="bid-img"
                      />
                      <div className="des">
                        {" "}
                        <p className="mb-0 pt-3">
                          {bid.jobId.description}{" "}
                          {/* <span className="read-more">...more</span> */}
                        </p>
                        <div className="bid-footer">
                          <span className="tags-label">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              className="tag-icon"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 6v6l4 4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Concerns
                          </span>

                          <div className="bid-tags">
                            {bid.jobId.concerns.map((tag, index) => (
                              <span key={index} className="bid-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bid Footer */}
                  </div>
                  );
                })
              ) : (
                <div className="no-bids">
                  <p>No bids found in this category.</p>
                </div>
              )}
            </div>
          </Col>

          {/* Sidebar */}
          {AwardDetailShow && (
            <Col xl={4}>
              <AwardDetail
                bidData={selectedBidData}
                bidListData={bidsData}
                setBidsData={setBidsData}
                setActiveTab={setActiveTab}
                setAwardDetailShow={setAwardDetailShow}
              />
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default Bids;
